import { DestroyRef, inject, type Provider } from '@angular/core';
import { Inject, Optional, SkipSelf } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  ANALYTICS_VIEW_CONTEXT,
  ANALYTICS_VIEW_CONTEXT_LEAF,
  AnalyticsViewContext,
} from '@core/analytics-types';

import { GlobalAnalyticsContext } from './global-context.service';

export const analyticsContextFactory = (
  contextName: string,
  parentContext?: string[],
): string[] => {
  const globalContext = inject(GlobalAnalyticsContext);
  const destroyRef = inject(DestroyRef);
  const newContext = [...(parentContext || []), contextName];
  globalContext.registerContext(parentContext, contextName, newContext);
  destroyRef.onDestroy(() =>
    globalContext.unregisterContext(parentContext, contextName, newContext),
  );
  return newContext;
};

export function provideAnalyticsViewContext(viewContext: AnalyticsViewContext): Provider[] {
  return [
    {
      provide: ANALYTICS_VIEW_CONTEXT_LEAF,
      useValue: viewContext,
    },
    {
      provide: ANALYTICS_VIEW_CONTEXT,
      useFactory: analyticsContextFactory,
      deps: [
        [new Inject(ANALYTICS_VIEW_CONTEXT_LEAF)],
        [new SkipSelf(), new Optional(), new Inject(ANALYTICS_VIEW_CONTEXT)],
      ],
    },
  ];
}

export const analyticsRouterContextFactory = (
  contextFromRoute?: string,
  parentContext?: string[],
): string[] => [...(parentContext || []), contextFromRoute!].filter(Boolean);

export const analyticsContextFormRouterFactory = (route: ActivatedRoute) => {
  const viewContext: AnalyticsViewContext = route.snapshot.data.viewContext;
  return viewContext;
};

export function provideAnalyticsViewContextFromRouterData(): Provider[] {
  return [
    {
      provide: ANALYTICS_VIEW_CONTEXT_LEAF,
      useFactory: analyticsContextFormRouterFactory,
      deps: [ActivatedRoute],
    },
    {
      provide: ANALYTICS_VIEW_CONTEXT,
      useFactory: analyticsRouterContextFactory,
      deps: [
        [new Inject(ANALYTICS_VIEW_CONTEXT_LEAF)],
        [new SkipSelf(), new Optional(), new Inject(ANALYTICS_VIEW_CONTEXT)],
      ],
    },
  ];
}
